import React, { useState, useEffect } from 'react';
import { Star, User, ShieldCheck, ChevronDown, Loader2 } from 'lucide-react';
import reviewService from '../../services/reviewService';

const ProductReviews = ({ productId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const targetPage = loadMore ? page + 1 : 1;
    const result = await reviewService.getProductReviews(productId, { page: targetPage, limit: 5 });

    if (result.success) {
      if (loadMore && data) {
        setData({
          ...result.data,
          reviews: [...data.reviews, ...result.data.reviews],
        });
      } else {
        setData(result.data);
      }
      setPage(targetPage);
    }

    setLoading(false);
    setLoadingMore(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const offset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() + offset);
    return adjustedDate.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  const renderStars = (rating, size = 'w-4 h-4') => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${size} ${
            star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-20 bg-gray-100 rounded"></div>
          <div className="h-24 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || data.stats.totalReviews === 0) {
    return (
      <div className="p-6 bg-white rounded-2xl">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Customer Reviews</h3>
        <div className="text-center py-8">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No reviews yet</p>
          <p className="text-sm text-gray-400">Be the first to review this product</p>
        </div>
      </div>
    );
  }

  const { reviews, stats, pagination } = data;
  const hasMore = pagination.page < pagination.pages;

  return (
    <div className="p-6 bg-white rounded-2xl">
      <h3 className="text-lg font-bold text-gray-800 mb-4">Customer Reviews</h3>

      {/* Stats Summary */}
      <div className="flex items-start gap-6 mb-6 p-4 bg-gray-50 rounded-xl">
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-800">{stats.averageRating}</div>
          <div className="flex justify-center my-1">
            {renderStars(Math.round(stats.averageRating), 'w-5 h-5')}
          </div>
          <div className="text-sm text-gray-500">{stats.totalReviews} reviews</div>
        </div>

        <div className="flex-1 space-y-1">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = stats.ratingDistribution[rating] || 0;
            const percentage = stats.totalReviews > 0
              ? (count / stats.totalReviews) * 100
              : 0;
            return (
              <div key={rating} className="flex items-center gap-2 text-sm">
                <span className="w-3 text-gray-600">{rating}</span>
                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="w-8 text-right text-gray-500">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                {review.user?.avatar && !review.user.avatar.includes('dicebear.com') ? (
                  <img
                    src={review.user.avatar}
                    alt={review.user.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#f2f8eb] flex items-center justify-center">
                    <User className="w-5 h-5 text-[#548B2E]" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{review.user?.name || 'Anonymous'}</span>
                    {review.isVerifiedPurchase && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                        <ShieldCheck className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{formatDate(review.createdAt)}</div>
                </div>
              </div>
              {renderStars(review.rating)}
            </div>

            {review.title && (
              <h4 className="font-medium text-gray-800 mb-1">{review.title}</h4>
            )}
            {review.comment && (
              <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>
            )}
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => fetchReviews(true)}
          disabled={loadingMore}
          className="w-full mt-4 py-2 flex items-center justify-center gap-2 text-[#548B2E] hover:text-[#3f6b20] transition-colors disabled:opacity-50"
        >
          {loadingMore ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          Load More Reviews
        </button>
      )}
    </div>
  );
};

export default ProductReviews;